// A line model for the JSON viewer: one entry per line that `JSON.stringify(value, null, 2)` would
// print, with the syntax already split into typed tokens and the block structure recorded so any
// line that opens a brace can be folded.
//
// Why a model rather than a regex over the printed text. Folding needs to know which line closes
// which, and a highlighter that regexes the output has to re-guess on every line whether a quoted
// run is a key or a value — `"$.NetImpact.Score"` reads as either. Walking the value knows both for
// free, and knows the nesting depth without counting braces.
//
// The invariant the tests hold this to: joining every line's indent and token text back together
// reproduces `JSON.stringify(value, null, 2)` byte for byte. The pane is a rendering of the JSON,
// not a paraphrase of it — an operator reading a spec here has to be reading the real document, and
// what the Copy button hands them has to be the thing they were looking at.

const isPlainObject = (v) => v !== null && typeof v === 'object' && !Array.isArray(v);

// Mirrors JSON.stringify's own rendering of a scalar, including the cases it has no representation
// for: a non-finite number prints as `null`, and so does anything that reached an array slot without
// being JSON at all.
const scalarToken = (value) => {
  if (value === null) return { t: 'literal', v: 'null' };
  if (typeof value === 'string') return { t: 'string', v: JSON.stringify(value) };
  if (typeof value === 'number') return { t: 'number', v: Number.isFinite(value) ? String(value) : 'null' };
  if (typeof value === 'boolean') return { t: 'literal', v: String(value) };
  return { t: 'literal', v: 'null' };
};

// An object key whose value stringify drops entirely, rather than printing as null. Arrays are the
// other way round — a hole there becomes `null` — which scalarToken above already handles.
const isDropped = (v) => v === undefined || typeof v === 'function' || typeof v === 'symbol';

const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;

// `key === null` means an array element, which prints without a label. Object keys always arrive as
// strings, so the sentinel cannot collide with a real one — not even with the key "null".
const emit = (lines, value, key, depth, trailing) => {
  const head =
    key === null
      ? []
      : [
          { t: 'key', v: JSON.stringify(key) },
          { t: 'punct', v: ': ' },
        ];
  const isArray = Array.isArray(value);

  if (!isArray && !isPlainObject(value)) {
    const tail = trailing ? [{ t: 'punct', v: trailing }] : [];
    lines.push({ depth, tokens: head.concat([scalarToken(value)], tail), open: false, close: null });
    return;
  }

  const entries = isArray
    ? value.map((item) => [null, item])
    : Object.keys(value)
        .filter((k) => !isDropped(value[k]))
        .map((k) => [k, value[k]]);
  const openBrace = isArray ? '[' : '{';
  const closeBrace = isArray ? ']' : '}';

  // stringify prints an empty container inline — `{}`, not `{` then `}` — so it is one line here too.
  if (entries.length === 0) {
    const tokens = head.concat([{ t: 'punct', v: `${openBrace}${closeBrace}${trailing}` }]);
    lines.push({ depth, tokens, open: false, close: null });
    return;
  }

  const openIndex = lines.length;
  lines.push({
    depth,
    tokens: head.concat([{ t: 'punct', v: openBrace }]),
    open: true,
    close: null,
    // What the fold shows in place of the block, and the closing brace it has to carry with it —
    // a folded line ends in `},` when the block it stands for was followed by a sibling.
    summary: isArray ? plural(entries.length, 'item', 'items') : plural(entries.length, 'key', 'keys'),
    closeText: `${closeBrace}${trailing}`,
  });

  entries.forEach(([entryKey, entryValue], i) => {
    emit(lines, entryValue, entryKey, depth + 1, i === entries.length - 1 ? '' : ',');
  });

  lines.push({ depth, tokens: [{ t: 'punct', v: `${closeBrace}${trailing}` }], open: false, close: null });
  lines[openIndex].close = lines.length - 1;
};

export const jsonLines = (value) => {
  const lines = [];
  emit(lines, value, null, 0, '');
  return lines;
};

// One line back as plain text. The viewer renders the indent as padding instead (so a wrapped line
// keeps its indentation), so this exists for the round-trip test and for anything that wants the
// listing as text.
export const lineText = (line) => '  '.repeat(line.depth) + line.tokens.map((token) => token.v).join('');
